<template>
  <DefaultLayout />
  <PvToast />
  <ConfirmDialog />
</template>

<script setup lang="ts">
import { useHead } from "@unhead/vue";
import { useCrisp } from "shared-ui/composables/use-crisp.ts";
import { createCookieConsentLanguageUpdater } from "shared-ui/utils/cookie-consent-language.ts";
import { createCookieConsentConfig } from "shared-ui/utils/create-cookie-consent-config.ts";
import { loadCookieConsentSafely } from "shared-ui/utils/load-cookie-consent.ts";
import { computed, defineAsyncComponent, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import DefaultLayout from "@/layouts/DefaultLayout.vue";

const { t } = useI18n();
const router = useRouter();
// The route commits after beforeResolve has finished loading its translations.
const consentLocale = computed(() =>
  router.currentRoute.value.params.locale === "sv" ? "sv" : "en",
);
let updateConsentLanguage: ((language: string) => Promise<void>) | undefined;

watch(consentLocale, async (language) => {
  await updateConsentLanguage?.(language);
});

const PvToast = defineAsyncComponent(() => import("primevue/toast"));
const ConfirmDialog = defineAsyncComponent(
  () => import("primevue/confirmdialog"),
);

const canonicalUrl = computed(
  () =>
    new URL(
      router.currentRoute.value.path.replace(/\/+$/, "") || "/",
      import.meta.env.VITE_PUBLIC_SITE_URL,
    ).href,
);

useHead(() => ({
  title: "Loading",
  titleTemplate: "%s | Controla",
  meta: [
    { property: "og:url", content: canonicalUrl.value },
    { property: "og:type", content: "website" },
  ],
  link: [
    {
      rel: "canonical",
      href: canonicalUrl.value,
    },
  ],
}));

async function loadCookieConsent(): Promise<void> {
  const [, CookieConsent] = await Promise.all([
    import("shared-ui/assets/cookie-consent.css"),
    import("vanilla-cookieconsent"),
  ]);

  const cookieConsentOptions = createCookieConsentConfig(
    (key, language) => t(key, {}, { locale: language }),
    consentLocale.value,
    window.location.hostname,
    (language) =>
      router.resolve({
        name: "privacy-policy-locale",
        params: { locale: language },
      }).href,
  );
  await CookieConsent.run(cookieConsentOptions);
  updateConsentLanguage = createCookieConsentLanguageUpdater(
    CookieConsent.setLanguage,
  );
  await updateConsentLanguage(consentLocale.value);

  // Load Crisp chat only in production
  const { loadCrisp } = useCrisp();
  loadCrisp();
}

function scheduleCookieConsent(): void {
  const load = (): void => void loadCookieConsentSafely(loadCookieConsent);
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(load, { timeout: 2000 });
  } else {
    setTimeout(load, 0);
  }
}

onMounted(() => {
  if (document.readyState === "complete") {
    scheduleCookieConsent();
    return;
  }

  window.addEventListener("load", scheduleCookieConsent, { once: true });
});
</script>
